import { FileText, Maximize2 } from "lucide-react";

import { Estado } from "@/components/propuesta/piezas";
import { cn } from "@/lib/utils";
import { dinero, type Expediente, fecha, RAYA } from "./comun";

// ===========================================================================
// EL CONTEXTO DEL EXPEDIENTE: de quién es el pago y qué se emitió.
//
// Va arriba y ocupa poco, pero es lo que hace reconocible al pagador. Sin la
// ficha, un Yape de 600 soles firmado por un familiar no se puede atribuir a
// nadie: el nombre del que envía no es el de la boleta, y el teléfono es lo
// único que los une.
//
// El cuadre de la derecha contesta la pregunta que trae a esta pantalla —
// ¿está entero este expediente?— con tres números y una barra, y no con una
// tabla que haya que sumar con el dedo.
// ===========================================================================

export function TiraDelExpediente({
  datos,
  alAbrirPdf,
  alAbrirImagen,
}: {
  datos: Expediente;
  alAbrirPdf: (pagina: number) => void;
  /** Abre el recorte de la boleta en grande, como el del voucher. */
  alAbrirImagen: (recorte: string, titulo: string) => void;
}) {
  const { archivo, cliente, boletas, cuadre } = datos;
  const atados = datos.vouchers.filter((v) => v.enlace).length;
  const pendientes = datos.vouchers.filter(
    (v) => !v.enlace && !v.sinAbono,
  ).length;

  // El porcentaje sale de lo emitido y no de lo pagado: la pregunta contable
  // es cuánto de la venta está respaldado por un abono del banco.
  const base = cuadre.boletas || cuadre.vouchers || 1;
  const hecho = Math.min(100, Math.round((cuadre.atado / base) * 100));

  return (
    <section className="rounded-lg border bg-card shadow-sm">
      <div className="flex flex-wrap items-center gap-3 px-4 pt-3.5 pb-3">
        <FileText className="size-5 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <div className="truncate font-semibold text-[13.5px]">
            {archivo.nombre}
          </div>
          <div className="text-[11.5px] text-muted-foreground">
            {archivo.paginas ? `${archivo.paginas} páginas · ` : ""}
            {datos.vouchers.length}{" "}
            {datos.vouchers.length === 1 ? "voucher" : "vouchers"} ·{" "}
            {boletas.length} {boletas.length === 1 ? "boleta" : "boletas"}
          </div>
        </div>
        <button
          type="button"
          onClick={() => alAbrirPdf(1)}
          className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-[12.5px] transition-colors hover:bg-muted"
        >
          <Maximize2 className="size-3.5" />
          Abrir el PDF
        </button>
      </div>

      <div className="grid gap-4 border-t px-4 py-3.5 lg:grid-cols-3">
        {/* --- de quién es --------------------------------------------- */}
        <div>
          <Rotulo>El cliente, según la ficha</Rotulo>
          {cliente ? (
            <dl className="mt-2 space-y-1">
              <Dato clave="Nombre" valor={cliente.nombre} />
              <Dato clave="Documento" valor={cliente.documento} />
              <Dato
                clave={cliente.telefonos.length > 1 ? "Teléfonos" : "Teléfono"}
                valor={cliente.telefonos.join(" · ") || null}
              />
              <Dato clave="Distrito" valor={cliente.distrito} />
            </dl>
          ) : (
            <p className="mt-2 text-[12px] text-muted-foreground">
              Este expediente no trae ficha del cliente. Sin ella, un pago por
              celular no tiene con qué reconocerse: el teléfono es lo único que
              lo ata a esta persona.
            </p>
          )}
        </div>

        {/* --- qué se emitió ------------------------------------------- */}
        <div className="lg:border-l lg:pl-4">
          <Rotulo>Lo que se emitió</Rotulo>
          <div className="mt-1">
            {boletas.length === 0 ? (
              <p className="mt-1 text-[12px] text-muted-foreground">
                Todavía no se ha leído ninguna boleta de este expediente.
              </p>
            ) : (
              boletas.map((b) => (
                <div
                  key={b.id}
                  className="flex items-center gap-3 border-b border-dashed py-1.5 last:border-0"
                >
                  {/* EL TROCITO DE LA BOLETA, como el del voucher.
                      Se pidió con esas palabras: poder ver el papel tal cual
                      llegó sin abrir el PDF entero, y agrandarlo de un clic.
                      
                      Cuando el PDF trae la boleta como IMAGEN se enseña el
                      recorte; cuando la trae como texto —que es lo normal, y
                      entonces no hay ninguna imagen que recortar— se enseña la
                      hoja dibujada. El endpoint que la dibuja ya existe y tacha
                      los RUC del grupo, así que sirve igual. */}
                  {b.recorte || b.pagina ? (
                    <button
                      type="button"
                      onClick={() =>
                        b.recorte
                          ? alAbrirImagen(
                              b.recorte,
                              b.serieNumero ?? "la boleta",
                            )
                          : alAbrirPdf(b.pagina ?? 1)
                      }
                      className="shrink-0 cursor-zoom-in"
                      aria-label={`Ver ${b.serieNumero ?? "la boleta"} en grande`}
                    >
                      <img
                        src={
                          b.recorte
                            ? `/imagen/${b.recorte}`
                            : `/pagina/${archivo.id}/${b.pagina}`
                        }
                        alt=""
                        loading="lazy"
                        className="h-14 w-11 rounded border bg-muted object-cover object-top"
                      />
                    </button>
                  ) : null}
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={() => alAbrirPdf(b.pagina ?? 1)}
                      className="font-semibold text-[12.5px] hover:underline"
                    >
                      {b.serieNumero ?? "Sin número"}
                    </button>
                    <div className="text-[11px] text-muted-foreground">
                      {b.tipo === "nota_credito" ? "Nota de crédito" : "Boleta"}
                      {b.fechaEmision ? ` · ${fecha(b.fechaEmision)}` : ""}
                      {b.pagina ? (
                        <>
                          {" · "}
                          <button
                            type="button"
                            onClick={() => alAbrirPdf(b.pagina ?? 1)}
                            className="text-cyan-700 underline-offset-2 hover:underline dark:text-cyan-400"
                          >
                            ver la hoja {b.pagina}
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className="ml-auto text-right">
                    <div className="font-semibold text-[12.5px] tabular-nums">
                      {dinero(b.total, b.moneda)}
                    </div>
                    <span
                      className={cn(
                        "text-[11px]",
                        b.estadoTono === "ok"
                          ? "text-emerald-700 dark:text-emerald-400"
                          : b.estadoTono === "warn"
                            ? "text-amber-700 dark:text-amber-400"
                            : "text-muted-foreground",
                      )}
                    >
                      {b.estadoEtiqueta}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* --- cómo va -------------------------------------------------- */}
        <div className="lg:border-l lg:pl-4">
          <div className="flex items-center gap-2">
            <Rotulo>Cómo va el expediente</Rotulo>
            <span className="ml-auto">
              <Estado tono={pendientes === 0 ? "ok" : "warn"}>
                {pendientes === 0
                  ? "Todos los vouchers resueltos"
                  : `${pendientes} sin atar`}
              </Estado>
            </span>
          </div>

          <div className="mt-2.5 flex h-2 overflow-hidden rounded-full bg-muted">
            <span
              className="h-full bg-emerald-600"
              style={{ width: `${hecho}%` }}
            />
            <span
              className="h-full bg-amber-500"
              style={{ width: `${100 - hecho}%` }}
            />
          </div>

          <dl className="mt-2.5 space-y-1">
            <Dato
              clave="Suman las boletas"
              valor={dinero(cuadre.boletas, cuadre.moneda)}
            />
            <Dato
              clave="Suman los vouchers"
              valor={dinero(cuadre.vouchers, cuadre.moneda)}
            />
            <Dato
              clave="Atado al banco"
              valor={dinero(cuadre.atado, cuadre.moneda)}
              tono={cuadre.atado >= cuadre.boletas ? "ok" : undefined}
            />
          </dl>

          {cuadre.mezclaMonedas ? (
            <p className="mt-2 rounded bg-amber-500/15 px-2 py-1 text-[11.5px] text-amber-700 dark:text-amber-400">
              Este expediente mezcla soles y dólares: los totales de arriba no
              se pueden sumar entre sí. Hay que mirarlo voucher por voucher.
            </p>
          ) : null}

          {atados > 0 && pendientes === 0 ? null : (
            <p className="mt-2 text-[11px] text-muted-foreground">
              El cuadre no cierra el expediente por sí solo: lo que lo cierra es
              que cada voucher tenga su movimiento.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-semibold text-[10.5px] text-muted-foreground uppercase tracking-wider">
      {children}
    </span>
  );
}

function Dato({
  clave,
  valor,
  tono,
}: {
  clave: string;
  valor: string | null;
  tono?: "ok";
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[12.5px]">
      <dt className="shrink-0 text-muted-foreground">{clave}</dt>
      <dd
        className={cn(
          "truncate text-right font-medium tabular-nums",
          tono === "ok" && "text-emerald-700 dark:text-emerald-400",
        )}
        title={valor ?? undefined}
      >
        {valor || RAYA}
      </dd>
    </div>
  );
}
